/**
 * Suppliers Page - Gestão de Fornecedores (Diretiva Executiva)
 * 
 * Funcionalidades:
 * - CRUD completo de fornecedores
 * - Código obrigatório (FRN001, FRN002...)
 * - Histórico de preços por produto
 * - Comparação de preços entre fornecedores
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { useAuth } from '@/hooks/useAuth';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import { toast } from 'sonner';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useLanguage } from '@/i18n';
import { buildAuthHeaders } from '@/lib/authStorage';
import {
  Building2, Plus, Search, RefreshCw, Edit2, Trash2, Eye,
  Phone, Mail, MapPin, Clock, TrendingDown, TrendingUp,
  History, Package, DollarSign, AlertTriangle, CheckCircle, Upload, Download, CheckSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPLIERS_TABLE_COLUMN_WIDTHS = {
  selector: 52,
  code: 118,
  name: 220,
  nuit: 136,
  contact: 168,
  phone: 148,
  terms: 124,
  status: 116,
  actions: 132,
};

const api = {
  get: async (url) => {
    const res = await fetch(`${API_URL}${url}`, {
      headers: buildAuthHeaders()
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    return res.json();
  },
  post: async (url, data) => {
    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    return res.json();
  },
  postForm: async (url, formData) => {
    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: formData,
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    return res.json();
  },
  put: async (url, data) => {
    const res = await fetch(`${API_URL}${url}`, {
      method: 'PUT',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    return res.json();
  },
  delete: async (url) => {
    const res = await fetch(`${API_URL}${url}`, {
      method: 'DELETE',
      headers: buildAuthHeaders()
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    return res.json();
  },
  download: async (url, filename) => {
    const res = await fetch(`${API_URL}${url}`, {
      headers: buildAuthHeaders()
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Error');
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  }
};

export default function SuppliersPage() {
  const { t } = useLanguage();
  const { hasPermission, hasRole } = useAuth();
  const { getColumnStyle: getSuppliersColumnStyle, startColumnResize: startSuppliersColumnResize } = useResizableTableColumns({
    storageKey: 'cg-suppliers-list-columns',
    defaultWidths: SUPPLIERS_TABLE_COLUMN_WIDTHS,
    minWidth: 72,
  });
  const isAdmin = hasRole(['admin']);
  const [activeTab, setActiveTab] = useState('list');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, supplier: null });
  const [viewDialog, setViewDialog] = useState({ open: false, supplier: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, supplier: null, reason: '' });
  const [priceHistoryDialog, setPriceHistoryDialog] = useState({ open: false, supplierId: null, data: null });
  const [importDialog, setImportDialog] = useState({ open: false, file: null, upsert: true, importing: false });
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, confirmation: '', reason: '' });

  // Form state
  const [form, setForm] = useState({
    name: '',
    nuit: '',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    city: 'Maputo',
    country: 'Moçambique',
    payment_terms: '30 dias',
    notes: ''
  });

  const resetForm = () => {
    setForm({
      name: '',
      nuit: '',
      contact_name: '',
      phone: '',
      email: '',
      address: '',
      city: 'Maputo',
      country: 'Moçambique',
      payment_terms: '30 dias',
      notes: ''
    });
  };

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/pricing/suppliers?include_inactive=true&include_deleted=${showDeleted}`);
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  useEffect(() => {
    setSelectedSupplierIds([]);
  }, [showDeleted, searchTerm]);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Nome do fornecedor é obrigatório');
      return;
    }
    try {
      const result = await api.post('/api/pricing/suppliers', form);
      toast.success(`Fornecedor criado: ${result.code || 'OK'}`);
      setCreateDialog(false);
      resetForm();
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleEdit = async () => {
    if (!editDialog.supplier) return;
    try {
      await api.put(`/api/pricing/suppliers/${editDialog.supplier.id}`, {
        ...form,
        justification: 'Actualização de dados do fornecedor'
      });
      toast.success('Fornecedor actualizado');
      setEditDialog({ open: false, supplier: null });
      resetForm();
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.supplier || !deleteDialog.reason.trim()) {
      toast.error('Motivo é obrigatório');
      return;
    }
    try {
      await api.delete(`/api/pricing/suppliers/${deleteDialog.supplier.id}?reason=${encodeURIComponent(deleteDialog.reason)}`);
      toast.success('Fornecedor eliminado');
      setDeleteDialog({ open: false, supplier: null, reason: '' });
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleDirectAdminDelete = async (supplier) => {
    try {
      await api.delete(`/api/pricing/suppliers/${supplier.id}?reason=${encodeURIComponent('Eliminação directa pelo administrador')}`);
      toast.success('Fornecedor eliminado pelo administrador');
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleRestore = async (supplier) => {
    try {
      await api.post(`/api/pricing/suppliers/${supplier.id}/restore`, {});
      toast.success('Fornecedor recuperado');
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await api.download('/api/pricing/suppliers/template.xlsx', 'fornecedores_import_template.xlsx');
      toast.success('Template de fornecedores descarregado');
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleImportSuppliers = async () => {
    if (!importDialog.file) {
      toast.error('Seleccione um ficheiro .xlsx');
      return;
    }
    try {
      setImportDialog((prev) => ({ ...prev, importing: true }));
      const formData = new FormData();
      formData.append('file', importDialog.file);
      const result = await api.postForm(`/api/pricing/suppliers/import?upsert_by_code_or_name=${importDialog.upsert}`, formData);
      toast.success(`Importação concluída: ${result.created} criados, ${result.updated} actualizados`);
      if (result.total_errors > 0) {
        toast.warning(`Foram detectados ${result.total_errors} erros no ficheiro`);
      }
      setImportDialog({ open: false, file: null, upsert: true, importing: false });
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
      setImportDialog((prev) => ({ ...prev, importing: false }));
    }
  };

  const toggleSupplierSelection = (supplierId, checked) => {
    setSelectedSupplierIds((prev) => {
      if (checked) {
        return prev.includes(supplierId) ? prev : [...prev, supplierId];
      }
      return prev.filter((id) => id !== supplierId);
    });
  };

  const toggleSelectAllVisible = (checked) => {
    if (checked) {
      setSelectedSupplierIds(filteredSuppliers.map((supplier) => supplier.id));
      return;
    }
    setSelectedSupplierIds([]);
  };

  const handleBulkHardDelete = async () => {
    if (!selectedSupplierIds.length) {
      toast.error('Seleccione fornecedores para hard delete');
      return;
    }
    try {
      const result = await api.post('/api/pricing/suppliers/hard-delete-bulk', {
        ids: selectedSupplierIds,
        confirmation: hardDeleteDialog.confirmation,
        reason: hardDeleteDialog.reason,
      });

      const blockedCount = (result.blocked || []).length;
      toast.success(`Hard delete concluído: ${result.deleted} removidos${blockedCount ? `, ${blockedCount} bloqueados` : ''}`);
      setHardDeleteDialog({ open: false, confirmation: '', reason: '' });
      setSelectedSupplierIds([]);
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleBulkStatus = async (action) => {
    if (!selectedSupplierIds.length) {
      toast.error('Seleccione fornecedores para ação em massa');
      return;
    }
    try {
      const result = await api.post('/api/pricing/suppliers/bulk-status', {
        ids: selectedSupplierIds,
        action,
        reason: `Ação em massa: ${action}`,
      });
      toast.success(result.message || 'Ação em massa concluída');
      setSelectedSupplierIds([]);
      loadSuppliers();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleExportSelectedSuppliers = () => {
    if (!selectedSupplierIds.length) {
      toast.error('Seleccione fornecedores para exportar');
      return;
    }
    const selected = suppliers.filter((supplier) => selectedSupplierIds.includes(supplier.id));
    if (!selected.length) {
      toast.error('Nenhum fornecedor seleccionado para exportação');
      return;
    }

    const headers = ['Código', 'Nome', 'NUIT', 'Contacto', 'Telefone', 'Email', 'Cidade', 'Prazo', 'Estado'];
    const lines = selected.map((supplier) => [
      supplier.code || '',
      supplier.name || '',
      supplier.nuit || '',
      supplier.contact_name || '-',
      supplier.phone || '-',
      supplier.email || '-',
      supplier.city || '-',
      supplier.payment_terms || '-',
      supplier.is_deleted ? 'Eliminado' : (supplier.is_active ? 'Activo' : 'Inactivo'),
    ]);

    const csvContent = [headers, ...lines]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `fornecedores_seleccionados_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} fornecedores exportados`);
  };

  const openEditDialog = (supplier) => {
    setForm({
      name: supplier.name || '',
      nuit: supplier.nuit || '',
      contact_name: supplier.contact_name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      city: supplier.city || 'Maputo',
      country: supplier.country || 'Moçambique',
      payment_terms: supplier.payment_terms || '30 dias',
      notes: supplier.notes || ''
    });
    setEditDialog({ open: true, supplier });
  };

  const loadSupplierDetails = async (supplier) => {
    try {
      const data = await api.get(`/api/pricing/suppliers/${supplier.id}`);
      setViewDialog({ open: true, supplier: data });
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-MZ', {
      style: 'currency',
      currency: 'MZN'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.nuit?.includes(searchTerm)
  );

  const allVisibleSelected = filteredSuppliers.length > 0 && filteredSuppliers.every((supplier) => selectedSupplierIds.includes(supplier.id));

  const canCreateSuppliers = hasPermission('suppliers.create');
  const canEditSuppliers = hasPermission('suppliers.edit');
  const canDeleteSuppliers = hasPermission('suppliers.delete');
  const canRecoverSuppliers = hasPermission('suppliers.recover');

  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <ModulePageHero
        testId="suppliers-page-hero"
        title={t('suppliersPage.title')}
        description={t('suppliersPage.description')}
        icon={Building2}
        badge={t('suppliersPage.badge')}
        compact
        stats={[
          { label: t('suppliersPage.stats.total'), value: suppliers.length, helper: t('suppliersPage.stats.totalHelper') },
          { label: t('suppliersPage.stats.active'), value: suppliers.filter(s => s.is_active).length, helper: t('suppliersPage.stats.activeHelper') },
          { label: t('suppliersPage.stats.inactive'), value: suppliers.filter(s => !s.is_active).length, helper: t('suppliersPage.stats.inactiveHelper') },
          { label: t('suppliersPage.stats.lastCode'), value: suppliers[0]?.code || 'FRN000', helper: t('suppliersPage.stats.lastCodeHelper') },
        ]}
        actions={(
          <>
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              data-testid="download-suppliers-template-button"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('suppliersPage.templateExcel')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setImportDialog({ open: true, file: null, upsert: true, importing: false })}
              data-testid="open-suppliers-import-dialog-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('suppliersPage.importExcel')}
            </Button>
            <Button
              variant="outline"
              disabled={!selectedSupplierIds.length}
              onClick={() => handleBulkStatus('deactivate')}
              data-testid="suppliers-bulk-deactivate-button"
            >
              {t('suppliersPage.deactivate')}
            </Button>
            <Button
              variant="outline"
              disabled={!selectedSupplierIds.length}
              onClick={() => handleBulkStatus('restore_soft')}
              data-testid="suppliers-bulk-restore-button"
            >
              {t('actions.restore')}
            </Button>
            <Button
              variant="outline"
              disabled={!selectedSupplierIds.length}
              onClick={handleExportSelectedSuppliers}
              data-testid="suppliers-bulk-export-button"
            >
              {t('actions.export')}
            </Button>
            {isAdmin && (
              <Button
                variant="destructive"
                disabled={!selectedSupplierIds.length}
                onClick={() => setHardDeleteDialog({ open: true, confirmation: '', reason: '' })}
                data-testid="open-suppliers-hard-delete-dialog-button"
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Hard Delete ({selectedSupplierIds.length})
              </Button>
            )}
            <Button
              variant={showDeleted ? 'default' : 'outline'}
              onClick={() => setShowDeleted((prev) => !prev)}
              data-testid="toggle-supplier-deleted-button"
            >
              {showDeleted ? t('suppliersPage.hideDeleted') : t('suppliersPage.showDeleted')}
            </Button>
            {canCreateSuppliers && (
              <Button onClick={() => { resetForm(); setCreateDialog(true); }} data-testid="create-supplier-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('suppliersPage.newSupplier')}
              </Button>
            )}
          </>
        )}
      />

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3 rounded-[24px] border border-slate-200 bg-white p-3 shadow-sm">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('suppliersPage.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 pl-9 text-sm"
          />
        </div>
        <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[10px] text-slate-700">
          {filteredSuppliers.length} visíveis
        </Badge>
        <Button variant="outline" size="sm" onClick={loadSuppliers}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('actions.refresh')}
        </Button>
      </div>

      {/* List */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="px-3 py-2.5">
          <CardTitle className="text-sm font-semibold">{t('suppliersPage.listTitle')}</CardTitle>
          <CardDescription className="text-xs">{t('suppliersPage.listCount', { count: filteredSuppliers.length })}</CardDescription>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('suppliersPage.noSuppliersFound')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[560px]">
              <table className="w-full text-[11px] leading-tight">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="border-b">
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('selector')}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                        data-testid="suppliers-select-all-checkbox"
                      />
                      <ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('selector', event)} testId="suppliers-column-resize-selector" />
                    </th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('code')}>Código<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('code', event)} testId="suppliers-column-resize-code" /></th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('name')}>Nome<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('name', event)} testId="suppliers-column-resize-name" /></th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('nuit')}>NUIT<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('nuit', event)} testId="suppliers-column-resize-nuit" /></th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('contact')}>Contacto<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('contact', event)} testId="suppliers-column-resize-contact" /></th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('phone')}>Telefone<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('phone', event)} testId="suppliers-column-resize-phone" /></th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('terms')}>Prazo<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('terms', event)} testId="suppliers-column-resize-terms" /></th>
                    <th className="relative text-left py-3 px-4" style={getSuppliersColumnStyle('status')}>Estado<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('status', event)} testId="suppliers-column-resize-status" /></th>
                    <th className="relative text-right py-3 px-4" style={getSuppliersColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startSuppliersColumnResize('actions', event)} testId="suppliers-column-resize-actions" /></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="border-b hover:bg-gray-50" data-testid={`supplier-row-${supplier.id}`}>
                      <td className="py-3 px-4" style={getSuppliersColumnStyle('selector')}>
                        <input
                          type="checkbox"
                          checked={selectedSupplierIds.includes(supplier.id)}
                          onChange={(e) => toggleSupplierSelection(supplier.id, e.target.checked)}
                          data-testid={`supplier-select-checkbox-${supplier.id}`}
                        />
                      </td>
                      <td className="py-3 px-4" style={getSuppliersColumnStyle('code')}>
                        <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {supplier.code}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium" style={getSuppliersColumnStyle('name')}>{supplier.name}</td>
                      <td className="py-3 px-4 text-sm" style={getSuppliersColumnStyle('nuit')}>{supplier.nuit || '-'}</td>
                      <td className="py-3 px-4 text-sm" style={getSuppliersColumnStyle('contact')}>{supplier.contact_name || '-'}</td>
                      <td className="py-3 px-4 text-sm" style={getSuppliersColumnStyle('phone')}>{supplier.phone || '-'}</td>
                      <td className="py-3 px-4 text-sm" style={getSuppliersColumnStyle('terms')}>{supplier.payment_terms || '-'}</td>
                      <td className="py-3 px-4" style={getSuppliersColumnStyle('status')}>
                        {supplier.is_deleted ? (
                          <Badge className="bg-red-100 text-red-800">Apagado</Badge>
                        ) : supplier.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Activo</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800">Inactivo</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right" style={getSuppliersColumnStyle('actions')}>
                        <UniversalCrudActions
                          entityLabel="supplier"
                          entityId={supplier.id}
                          isDeleted={supplier.is_deleted}
                          canView
                          canEdit={canEditSuppliers}
                          canDelete={canDeleteSuppliers}
                          canRecover={canRecoverSuppliers}
                          onView={() => loadSupplierDetails(supplier)}
                          onEdit={() => openEditDialog(supplier)}
                          onDelete={() => isAdmin ? handleDirectAdminDelete(supplier) : setDeleteDialog({ open: true, supplier, reason: '' })}
                          onRecover={() => handleRestore(supplier)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo Fornecedor</DialogTitle>
            <DialogDescription>O código será gerado automaticamente (FRN001, FRN002...)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome do fornecedor"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>NUIT</Label>
                <Input
                  value={form.nuit}
                  onChange={(e) => setForm({ ...form, nuit: e.target.value })}
                  placeholder="000000000"
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+258 84..."
                />
              </div>
            </div>
            <div>
              <Label>Pessoa de Contacto</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="Nome do contacto"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@fornecedor.com"
              />
            </div>
            <div>
              <Label>Morada</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Endereço"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cidade</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Maputo"
                />
              </div>
              <div>
                <Label>Prazo Pagamento</Label>
                <Select
                  value={form.payment_terms}
                  onValueChange={(v) => setForm({ ...form, payment_terms: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="À vista">À vista</SelectItem>
                    <SelectItem value="15 dias">15 dias</SelectItem>
                    <SelectItem value="30 dias">30 dias</SelectItem>
                    <SelectItem value="45 dias">45 dias</SelectItem>
                    <SelectItem value="60 dias">60 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Observações..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-btn">
              <Plus className="h-4 w-4 mr-2" />
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, supplier: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Fornecedor</DialogTitle>
            <DialogDescription>Código: {editDialog.supplier?.code}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>NUIT</Label>
                <Input
                  value={form.nuit}
                  onChange={(e) => setForm({ ...form, nuit: e.target.value })}
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Pessoa de Contacto</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Morada</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cidade</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div>
                <Label>Prazo Pagamento</Label>
                <Select
                  value={form.payment_terms}
                  onValueChange={(v) => setForm({ ...form, payment_terms: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="À vista">À vista</SelectItem>
                    <SelectItem value="15 dias">15 dias</SelectItem>
                    <SelectItem value="30 dias">30 dias</SelectItem>
                    <SelectItem value="45 dias">45 dias</SelectItem>
                    <SelectItem value="60 dias">60 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, supplier: null })}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} data-testid="confirm-edit-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW DIALOG */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, supplier: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Fornecedor</DialogTitle>
          </DialogHeader>
          {viewDialog.supplier && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{viewDialog.supplier.name}</h3>
                  <p className="font-mono text-blue-600">{viewDialog.supplier.code}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">NUIT</p>
                  <p className="font-medium">{viewDialog.supplier.nuit || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Contacto</p>
                  <p className="font-medium">{viewDialog.supplier.contact_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Telefone</p>
                  <p className="font-medium">{viewDialog.supplier.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{viewDialog.supplier.email || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Morada</p>
                  <p className="font-medium">
                    {viewDialog.supplier.address || '-'}, {viewDialog.supplier.city}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Prazo Pagamento</p>
                  <p className="font-medium">{viewDialog.supplier.payment_terms || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Estado</p>
                  {viewDialog.supplier.is_active ? (
                    <Badge className="bg-green-100 text-green-800">Activo</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800">Inactivo</Badge>
                  )}
                </div>
              </div>
              {viewDialog.supplier.stats && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Estatísticas</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Produtos c/ Preço</p>
                      <p className="font-bold text-lg">{viewDialog.supplier.stats.products_count}</p>
                    </div>
                  </div>
                </div>
              )}
              {viewDialog.supplier.notes && (
                <div className="border-t pt-4">
                  <p className="text-muted-foreground text-sm">Notas</p>
                  <p>{viewDialog.supplier.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, supplier: null })}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, supplier: null, reason: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Fornecedor</DialogTitle>
            <DialogDescription>
              Esta acção irá desactivar o fornecedor e todos os seus preços.
            </DialogDescription>
          </DialogHeader>
          {deleteDialog.supplier && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="font-medium">{deleteDialog.supplier.name}</p>
                <p className="text-sm text-muted-foreground">{deleteDialog.supplier.code}</p>
              </div>
              <div>
                <Label>Motivo da eliminação *</Label>
                <Textarea
                  value={deleteDialog.reason}
                  onChange={(e) => setDeleteDialog({ ...deleteDialog, reason: e.target.value })}
                  placeholder="Explique o motivo..."
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, supplier: null, reason: '' })}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IMPORT DIALOG */}
      <Dialog
        open={importDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setImportDialog({ open: false, file: null, upsert: true, importing: false });
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar Fornecedores por Excel</DialogTitle>
            <DialogDescription>
              Use o template oficial para adicionar/actualizar fornecedores em massa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ficheiro .xlsx</Label>
              <Input
                type="file"
                accept=".xlsx"
                onChange={(e) => setImportDialog((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                data-testid="suppliers-import-file-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="suppliers-import-upsert-checkbox"
                type="checkbox"
                checked={importDialog.upsert}
                onChange={(e) => setImportDialog((prev) => ({ ...prev, upsert: e.target.checked }))}
                data-testid="suppliers-import-upsert-checkbox"
              />
              <Label htmlFor="suppliers-import-upsert-checkbox">Actualizar por código/nome quando já existir</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialog({ open: false, file: null, upsert: true, importing: false })}>Cancelar</Button>
            <Button
              onClick={handleImportSuppliers}
              disabled={importDialog.importing}
              data-testid="suppliers-import-confirm-button"
            >
              {importDialog.importing ? 'A importar...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HARD DELETE BULK DIALOG */}
      <AlertDialog open={hardDeleteDialog.open} onOpenChange={(open) => !open && setHardDeleteDialog({ open: false, confirmation: '', reason: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hard Delete em Massa (Fornecedores)</AlertDialogTitle>
            <AlertDialogDescription>
              Acção irreversível. Registos seleccionados serão removidos definitivamente se não tiverem dependências.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">Seleccionados: <strong>{selectedSupplierIds.length}</strong></p>
            <div>
              <Label>Confirmação *</Label>
              <Input
                value={hardDeleteDialog.confirmation}
                onChange={(e) => setHardDeleteDialog((prev) => ({ ...prev, confirmation: e.target.value }))}
                placeholder="Digite: APAGAR DEFINITIVAMENTE"
                data-testid="suppliers-hard-delete-confirmation-input"
              />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Textarea
                value={hardDeleteDialog.reason}
                onChange={(e) => setHardDeleteDialog((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Registe o motivo da remoção permanente"
                rows={3}
                data-testid="suppliers-hard-delete-reason-input"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkHardDelete}
              className="bg-red-700 hover:bg-red-800"
              data-testid="suppliers-hard-delete-confirm-button"
            >
              Hard Delete Definitivo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
